/**
 * The library's wiring: the catalogue, what the trainer has asked of it, and the answer.
 *
 * Decides nothing. The filtering, the counts, the Polish folding and the collation all live in this
 * slice's `exerciseCatalogue`, which is where a test can reach them.
 *
 * ── It was `features/exercise-library-rail/lib/useExerciseRail` until 17 September 2026 ───────
 * It moved down here with `ExerciseLibraryList`, when the survey editor's recording picker became
 * the same library in a dialog (owner, 17 September 2026). Two things did NOT come with it and stay
 * in the rail, because they are the plan builder's and no other host has a use for them: the muscle
 * ATTRIBUTION map the day-affinity default is computed from, and the lifted/not-lifted flag that
 * default is switched by. `items` is exported instead, so the rail builds the attribution out of the
 * same read the results come from and nothing is fetched twice.
 *
 * ── The query is state, the answer is derived ────────────────────────────────────────────────
 * One `useState` holding the whole `ExerciseQuery` rather than five holding its parts. They move
 * together — every one of them re-runs the same computation — so splitting them would be five
 * setters that each have to remember not to drop the other four, which is exactly how a facet
 * survives a search and a search does not survive a facet.
 *
 * ── No debounce, and that is measured rather than assumed ────────────────────────────────────
 * The catalogue is fetched whole and filtered in memory: 477 rows through four predicates and one
 * collated sort is well under a frame, and a debounce would put the trainer's own typing behind a
 * timer for no gain. If the catalogue ever grows past the point where that holds, the fix is a
 * measurement first — not a delay added on instinct.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { exerciseLibraryQueryOptions } from '../api/exerciseQueries';
import {
  exerciseCatalogue,
  type ExerciseCatalogueView,
  type ExerciseQuery,
} from '../model/exerciseCatalogue';
import type { ExerciseFacetKey } from '../model/exerciseFacets';
import { toExerciseListItems, type ExerciseListItem } from '../model/exerciseListItem';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import { useExerciseQuery } from './useExerciseQuery';
import { useOtherLanguageNames } from './useOtherLanguageNames';

export interface ExerciseLibraryState {
  readonly query: ExerciseQuery;
  readonly catalogue: ExerciseCatalogueView;
  /**
   * Every movement the read returned, before the query narrowed anything.
   *
   * The plan builder's day-affinity default is computed from these — through
   * `toMuscleAttribution` — so the day's load and the library's offer can never come from two
   * different catalogues. No other host reads it.
   */
  readonly items: readonly ExerciseListItem[];
  /** True while the first read is in flight — the surface says so rather than reading as empty. */
  readonly isPending: boolean;
  /** True when the read failed. The surface offers a retry; nothing it sits on is touched. */
  readonly isError: boolean;
  /**
   * The other content language's names could not be read, so the search answered in the displayed
   * language alone. The surface says so under a „nothing matched" — nowhere else.
   */
  readonly otherLanguageFailed: boolean;
  readonly retry: () => void;
  readonly setText: (text: string) => void;
  /**
   * A chip press, in any of the four groups. ONE dispatch rather than four typed toggles: which
   * facet an id belongs to and how it parses is this slice's decision, and the surface renders the
   * groups as data.
   */
  readonly toggle: (facet: ExerciseFacetKey, id: string) => void;
  /** The × on an active-filter chip. Distinct from `toggle` only for single-select ORIGIN. */
  readonly remove: (facet: ExerciseFacetKey, id: string) => void;
  readonly clear: () => void;
}

export interface ExerciseLibraryQueryOptions {
  /**
   * Whether the catalogue is read at all, and the survey editor is why it is a parameter.
   *
   * The catalogue is the app's largest read and the editor holds a dialog that may never be opened,
   * so a form with no recording question must not fetch 477 movements. A disabled query is
   * `isPending` for ever, which would draw the loading notice under a closed dialog — so the flag is
   * folded into `isPending` here rather than left for each caller to remember.
   */
  readonly enabled?: boolean;
  /**
   * Whether the library is ON SCREEN, which is what the SEARCH's second-language read waits for.
   * Defaults to `enabled`. The builder mounts its drawer shut so the catalogue is warm when it slides
   * out; that warm-up is the first read's job, and the other language is only worth fetching once a
   * trainer can type into the field.
   */
  readonly shown?: boolean;
}

export function useExerciseLibraryQuery({
  enabled = true,
  shown = enabled,
}: ExerciseLibraryQueryOptions = {}): ExerciseLibraryState {
  // The catalogue is ASKED FOR in the content language and COLLATED in the reader's own — two
  // variables, because a trainer reading an English library still sorts the way Polish sorts.
  const contentLang = useContentLanguage();
  const { i18n } = useTranslation();

  const { query, setText, toggle, remove, clear } = useExerciseQuery();
  const library = useQuery({ ...exerciseLibraryQueryOptions(contentLang), enabled });
  // The search also finds a movement by its name in the other language; see the hook.
  const otherNames = useOtherLanguageNames(enabled && shown);
  const items = toExerciseListItems(library.data);
  const catalogue = exerciseCatalogue(items, query, i18n.language, otherNames.names);

  return {
    query,
    catalogue,
    items,
    isPending: enabled && library.isPending,
    isError: library.isError,
    otherLanguageFailed: otherNames.failed,
    retry: () => {
      void library.refetch();
    },
    setText,
    toggle,
    remove,
    clear,
  };
}
